import React, { ChangeEvent, useState, useRef, useEffect } from "react";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import ReactModal from "react-modal";
import IAdminLoginDTO from "../../../models/DTO/AdminDTO/IAdminLoginDTO";
import IAdminLoginResultDTO from "../../../models/DTO/AdminDTO/IAdminLoginResultDTO";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import SocketService from "../../../services/SocketService";
import ky from "ky";

type Props = {
    closeModal: () => void;
    onLoginSuccess: (result: IAdminLoginResultDTO) => void;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
};

export const AdminLoginModal: React.FC<Props> = (props) => {
    const modalElem = useRef<HTMLElement | null>(null);
    const [modalIsOpen, setModalIsOpen] = useState(true);
    const [modalStyle] = useState({
        overlay: {
            zIndex: 999999999,
        },
        content: {},
    });
    const [loginActive, setLoginActive] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    useEffect(() => {
        modalElem.current = document.getElementById("root");
        if (modalElem.current) {
            ReactModal.setAppElement(modalElem.current as HTMLElement);
        }
    }, []);

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        switch (name) {
            case "email":
                setEmail(value);
                break;
            case "password":
                setPassword(value);
                break;
        }
    };

    const performAdminLogin = async () => {
        if (!email || !password) {
            const notification: INotification = {
                title: "validation_error",
                message: "email_and_password_required",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            };
            props.notifyFn(null, notification);
            return;
        }

        setLoginActive(true);

        const payload: IAdminLoginDTO = {
            email,
            password,
        };

        const apiUrl = SocketService.getEndpointUrl();
        const url = `${apiUrl}${RestEndpointsEnum.AdminLogin}`;

        try {
            const res = await ky.post(url, {
                json: payload,
            });

            const data: IAdminLoginResultDTO = await res.json();

            if (data.success) {
                const notification: INotification = {
                    title: "admin_login_successful",
                    message: `logged_in_as ${data.admin?.email}`,
                    level: NotificationLevelEnum.success,
                    position: NotificationPositionEnum.bc,
                };
                props.notifyFn(null, notification, true);

                props.onLoginSuccess(data);
                closeModal();
            } else {
                const notification: INotification = {
                    title: "admin_login_failed",
                    message: data.message || "invalid_credentials",
                    level: NotificationLevelEnum.error,
                    position: NotificationPositionEnum.bc,
                };
                props.notifyFn(null, notification, true);
                setLoginActive(false);
            }
        } catch (err) {
            console.log("Admin login error:", err);
            const notification: INotification = {
                title: "admin_login_error",
                message: "error_connecting_to_server",
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.bc,
            };
            props.notifyFn(null, notification);
            setLoginActive(false);
        }
    };

    const closeModal = () => {
        setModalIsOpen(false);
        props.closeModal();
    };

    return (
        <ReactModal
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            contentLabel="Admin Login"
            shouldCloseOnOverlayClick={true}
            className="modalContentClass"
            overlayClassName="modalOverlayClass"
            style={modalStyle}>
            <div className="p-4">
                <OwTitle title={"admin_login"} tag={"h3"} />

                <div className="mt-4">
                    <OwLabel title={"email"} />
                    <OwInputWrapper title={"admin email"} name={"email"} value={email} fn={handleChange} isRequired={true} tabIndex={1} />
                </div>

                <div className="mt-3">
                    <OwLabel title={"password"} />
                    <OwInputWrapper
                        title={"admin password"}
                        name={"password"}
                        value={password}
                        fn={handleChange}
                        type={"password"}
                        isRequired={true}
                        tabIndex={2}
                    />
                </div>

                <div className="mt-4 d-flex justify-content-between">
                    <OwButton fn={closeModal} title={"cancel"} className="btn-secondary" />
                    <OwButton fn={performAdminLogin} title={"login"} active={loginActive} disableOnActive={true} />
                </div>
            </div>
        </ReactModal>
    );
};
